import { cx } from '../internal/cx';

/** Spinner sizes in px: inside a button, standalone, and centred in an empty pane. */
export type SpinnerSize = 'sm' | 'md' | 'lg';

const SIZE_PX: Readonly<Record<SpinnerSize, number>> = { sm: 14, md: 20, lg: 32 };

/** Geometry of the ring, in the 24×24 user space of the `viewBox`. */
const VIEW_BOX = 24;
const RADIUS = 9;
const STROKE_WIDTH = 2.5;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
/** The moving arc covers a third of the ring; the rest stays as a faint track. */
const ARC_LENGTH = CIRCUMFERENCE / 3;
/** Opacity of that track, so the shape still reads as a ring while the arc travels. */
const TRACK_OPACITY = 0.2;

/** Props of {@link Spinner}. */
export interface SpinnerProps {
  /** Diameter; `md` (20 px) by default. */
  size?: SpinnerSize;
  /**
   * Accessible name of the progress indicator, e.g. "Yükleniyor". Give it when the spinner is the
   * only thing announcing that work is running; leave it out when a visible label already says so.
   */
  label?: string;
  /** Extra utility classes. The arc is drawn in the current text colour. */
  className?: string;
}

/**
 * An indeterminate progress indicator. It keeps turning under `prefers-reduced-motion`, four times
 * more slowly, because stopping it would remove the only sign that work is still running.
 *
 * @example <Spinner label={t('common.loading')} />
 */
export function Spinner({ size = 'md', label, className }: SpinnerProps) {
  const px = SIZE_PX[size];
  return (
    <svg
      width={px}
      height={px}
      viewBox={`0 0 ${VIEW_BOX} ${VIEW_BOX}`}
      fill="none"
      className={cx('ui-animate-spin shrink-0', className)}
      focusable="false"
      aria-hidden={label === undefined || undefined}
      role={label === undefined ? undefined : 'status'}
      aria-label={label}
    >
      <circle
        cx={VIEW_BOX / 2}
        cy={VIEW_BOX / 2}
        r={RADIUS}
        stroke="currentColor"
        strokeWidth={STROKE_WIDTH}
        opacity={TRACK_OPACITY}
      />
      <circle
        cx={VIEW_BOX / 2}
        cy={VIEW_BOX / 2}
        r={RADIUS}
        stroke="currentColor"
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
        strokeDasharray={`${ARC_LENGTH} ${CIRCUMFERENCE - ARC_LENGTH}`}
      />
    </svg>
  );
}
